import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { FaArchive, FaExternalLinkAlt, FaInbox, FaRedo, FaSyncAlt } from "react-icons/fa";
import { useAuth } from "../Login/AuthContext";

const ALERT_COLOR = "#92400e";

const AdminCuadrosPersonalizados = () => {
  const { token } = useAuth();
  const [solicitudes, setSolicitudes] = useState([]);
  const [filtro, setFiltro] = useState("pendiente");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const API_URL = process.env.REACT_APP_API_URL;
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

  const cargarSolicitudes = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await axios.get(`${API_URL}/admin/cuadros-personalizados`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { estado: "todos" },
      });
      const data = Array.isArray(response.data)
        ? response.data
        : response.data?.solicitudes || [];
      setSolicitudes(data);

      try {
        await axios.patch(
          `${API_URL}/admin/cuadros-personalizados/marcar-leidas`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        window.dispatchEvent(new Event("cuadros-personalizados-actualizados"));
      } catch (markError) {
        // El listado sigue siendo util aunque no se pueda actualizar el indicador.
      }
    } catch (requestError) {
      setError("No se pudieron cargar las solicitudes de cuadros personalizados.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarSolicitudes();
  }, [API_URL, token]);

  const solicitudesFiltradas = useMemo(() => {
    if (filtro === "todos") return solicitudes;
    return solicitudes.filter((item) => normalizarEstado(item.estado) === filtro);
  }, [filtro, solicitudes]);

  const resumen = useMemo(() => ({
    pendientes: solicitudes.filter((item) => normalizarEstado(item.estado) === "pendiente").length,
    archivadas: solicitudes.filter((item) => normalizarEstado(item.estado) === "archivado").length,
  }), [solicitudes]);

  const cambiarEstado = async (solicitud, estado) => {
    try {
      const response = await axios.patch(
        `${API_URL}/admin/cuadros-personalizados/${solicitud.id}/estado`,
        { estado },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const actualizada = response.data?.solicitud || response.data;
      setSolicitudes((prev) => prev.map((item) =>
        item.id === solicitud.id ? { ...item, ...actualizada, estado } : item
      ));
      window.dispatchEvent(new Event("cuadros-personalizados-actualizados"));
      Swal.fire({
        icon: "success",
        title: estado === "archivado" ? "Solicitud archivada" : "Solicitud reabierta",
        showConfirmButton: false,
        timer: 1300,
      });
    } catch (requestError) {
      Swal.fire({
        icon: "error",
        title: "No se pudo actualizar",
        text: "Intenta nuevamente.",
        confirmButtonColor: ALERT_COLOR,
      });
    }
  };

  return (
    <section className="min-h-screen bg-stone-50 px-4 py-8 md:px-8">
      <div className="mx-auto w-full max-w-7xl">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="text-sm font-semibold uppercase tracking-wide text-amber-700">
              Solicitudes de clientes
            </span>
            <h1 className="mt-2 text-3xl font-extrabold text-stone-900 md:text-4xl">
              Cuadros personalizados
            </h1>
            <p className="mt-2 text-stone-600">
              Revisa referencias y contacta a cada cliente para coordinar su obra.
            </p>
          </div>
          <button
            type="button"
            onClick={cargarSolicitudes}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-stone-900 px-4 py-3 text-sm font-semibold text-white transition hover:bg-amber-800"
          >
            <FaSyncAlt /> Actualizar
          </button>
        </div>

        <div className="mt-7 grid gap-4 sm:grid-cols-2">
          <Resumen label="Pendientes" value={resumen.pendientes} icon={<FaInbox />} />
          <Resumen label="Archivadas" value={resumen.archivadas} icon={<FaArchive />} />
        </div>

        <div className="mt-6 flex gap-2 overflow-x-auto rounded-xl border border-stone-200 bg-white p-2 shadow-sm">
          {["pendiente", "archivado", "todos"].map((estado) => (
            <button
              key={estado}
              type="button"
              onClick={() => setFiltro(estado)}
              className={`whitespace-nowrap rounded-lg px-4 py-2 text-sm font-bold transition ${
                filtro === estado ? "bg-stone-900 text-white" : "text-stone-600 hover:bg-stone-100"
              }`}
            >
              {estado === "todos" ? "Todas" : capitalizar(estado)}
            </button>
          ))}
        </div>

        {loading ? (
          <Estado texto="Cargando solicitudes..." />
        ) : error ? (
          <Estado texto={error} />
        ) : solicitudesFiltradas.length === 0 ? (
          <Estado texto="No hay solicitudes en esta seccion." />
        ) : (
          <div className="mt-6 grid gap-5 lg:grid-cols-2">
            {solicitudesFiltradas.map((solicitud) => (
              <SolicitudCard
                key={solicitud.id}
                solicitud={solicitud}
                backendUrl={BACKEND_URL}
                onCambiarEstado={cambiarEstado}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

const SolicitudCard = ({ solicitud, backendUrl, onCambiarEstado }) => {
  const estado = normalizarEstado(solicitud.estado);
  const cliente = solicitud.usuario || solicitud.cliente || {};
  const nombre = [cliente.nombre, cliente.apellido].filter(Boolean).join(" ")
    || cliente.username
    || solicitud.usuario_nombre
    || "Cliente";
  const telefono = solicitud.numero_contacto || cliente.telefono || "Sin numero";
  const imagenUrl = resolverImagen(solicitud.imagen_url, backendUrl);

  return (
    <article className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-lg">
      <div className="grid sm:grid-cols-[220px_1fr]">
        <div className="min-h-56 bg-stone-100">
          {imagenUrl ? (
            <a href={imagenUrl} target="_blank" rel="noreferrer" title="Abrir imagen">
              <img src={imagenUrl} alt="Referencia del cliente" className="h-full min-h-56 w-full object-cover" />
            </a>
          ) : (
            <div className="flex h-full min-h-56 items-center justify-center text-stone-400">
              Sin imagen
            </div>
          )}
        </div>

        <div className="flex flex-col p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-amber-700">
                Solicitud #{solicitud.id}
              </p>
              <h2 className="mt-1 text-xl font-extrabold text-stone-900">{nombre}</h2>
            </div>
            <span className={`rounded-full px-3 py-1 text-xs font-bold uppercase ${
              estado === "archivado" ? "bg-stone-200 text-stone-700" : "bg-amber-100 text-amber-800"
            }`}>
              {capitalizar(estado)}
            </span>
          </div>

          <p className="mt-4 whitespace-pre-wrap text-sm leading-6 text-stone-700">
            {solicitud.descripcion}
          </p>

          <div className="mt-5 space-y-2 border-t border-stone-200 pt-4 text-sm">
            <p><span className="font-bold text-stone-800">Contacto:</span> {telefono}</p>
            {cliente.email && <p><span className="font-bold text-stone-800">Email:</span> {cliente.email}</p>}
            <p><span className="font-bold text-stone-800">Enviada:</span> {formatearFecha(solicitud.created_at || solicitud.fecha_creacion)}</p>
          </div>

          <div className="mt-auto flex flex-wrap gap-2 pt-5">
            {telefono !== "Sin numero" && (
              <a
                href={`https://wa.me/${telefono.replace(/\D/g, "")}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-lg bg-emerald-700 px-3 py-2 text-sm font-bold text-white transition hover:bg-emerald-800"
              >
                Contactar <FaExternalLinkAlt className="text-xs" />
              </a>
            )}
            {estado === "archivado" ? (
              <button
                type="button"
                onClick={() => onCambiarEstado(solicitud, "pendiente")}
                className="inline-flex items-center gap-2 rounded-lg bg-stone-100 px-3 py-2 text-sm font-bold text-stone-800 transition hover:bg-stone-200"
              >
                <FaRedo /> Volver a pendientes
              </button>
            ) : (
              <button
                type="button"
                onClick={() => onCambiarEstado(solicitud, "archivado")}
                className="inline-flex items-center gap-2 rounded-lg bg-stone-900 px-3 py-2 text-sm font-bold text-white transition hover:bg-amber-800"
              >
                <FaArchive /> Archivar
              </button>
            )}
          </div>
        </div>
      </div>
    </article>
  );
};

const Resumen = ({ label, value, icon }) => (
  <div className="flex items-center gap-4 rounded-xl border border-stone-200 bg-white p-5 shadow-sm">
    <div className="rounded-full bg-amber-100 p-3 text-amber-800">{icon}</div>
    <div>
      <p className="text-xs font-bold uppercase tracking-wide text-stone-500">{label}</p>
      <p className="text-3xl font-extrabold text-stone-900">{value}</p>
    </div>
  </div>
);

const Estado = ({ texto }) => (
  <div className="mt-6 flex min-h-64 items-center justify-center rounded-xl border border-stone-200 bg-white p-6 text-center font-semibold text-stone-600">
    {texto}
  </div>
);

const normalizarEstado = (estado) => {
  const value = (estado || "pendiente").toString().toLowerCase();
  return value === "archivada" ? "archivado" : value;
};

const capitalizar = (text) => text ? text.charAt(0).toUpperCase() + text.slice(1) : "Pendiente";

const resolverImagen = (path, backendUrl) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return `${backendUrl}${path}`;
};

const formatearFecha = (value) => {
  if (!value) return "Sin fecha";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("es-UY", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default AdminCuadrosPersonalizados;
